import {AfterViewInit, Component, inject, input, InputSignal, signal, WritableSignal} from '@angular/core';
import {ObjectCopyService} from "../../../object-copy/object-copy.service";
import {SectionsContainer} from "../../../core/definitions/sections-container";
import {SharedModule} from "../../../shared/shared.module";
import {MetaField} from "../../../core/definitions/meta-field";
import {MatButton} from "@angular/material/button";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {SectionViewCopy} from "../../../object-copy/section-view-copy/section-view-copy";
import {EditObjectDialogComponent} from "../../edit-object-dialog/edit-object-dialog.component";
import {ObjectStorageService} from "../../../core/object-storage.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {CmsApiService} from "../../../core/cms-api.service";
import {ModelSchema} from "../../../core/definitions/model-schema";
import {Setting} from "../../../core/definitions/setting";
import {AlertDialogComponent} from "../../../shared/alert-dialog/alert-dialog.component";
import {ConfirmDialogData} from "../../confirm-dialog/confirm-dialog.component";
import {TranslateService} from "@ngx-translate/core";
import {GenerateIdentifierParams} from "../../../core/definitions/generate-identifier-params";

@Component({
  selector: 'app-consists-of-parent-copy',
  imports: [
    SharedModule,
    MatButton,
    MatProgressSpinner,
    SectionViewCopy
  ],
  templateUrl: './consists-of-parent-copy.html',
  styleUrl: './consists-of-parent-copy.scss',
})
export class ConsistsOfParentCopy implements AfterViewInit {
  private readonly cmsApi: CmsApiService = inject(CmsApiService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly objectCopyService: ObjectCopyService = inject(ObjectCopyService);
  private readonly objectStoreService: ObjectStorageService = inject(ObjectStorageService);

  dialogRef: InputSignal<MatDialogRef<EditObjectDialogComponent>> = input();
  sectionsContainer: InputSignal<SectionsContainer> = input();
  sourceArtifactId: InputSignal<string> = input();
  targetCount: InputSignal<number> = input(1);

  clientSettings: WritableSignal<Setting[]> = signal([]);
  loading: WritableSignal<boolean> = signal(false);
  errorMessage: WritableSignal<string> = signal(null);
  modelSchema: WritableSignal<ModelSchema> = signal(null);

  ngAfterViewInit() {
    this.cmsApi.getModelSchema({
      modelName: this.sectionsContainer().rootObject.object_type
    }).then((schema) => {
      this.modelSchema.set(schema);
      return this.cmsApi.getSettings();
    }).then((settings) => {
      this.clientSettings.set(settings);
    });

    for (const section of this.sectionsContainer().sections) {
      if (section.fields?.length) {
        for (const field of section.fields) {
          if (field.is_required === 'is_required' || field.key === 'identification_number') {
            this.objectCopyService.toggleField(true, field);
          }
        }
      }
    }
  }

  get anyFieldIsSelected(): boolean {
    return !!this.objectCopyService.selectedFields().length;
  }

  cancel(): void {
    this.dialogRef().close(false);
  }

  async createAndConnectObject() {
    this.loading.set(true);
    const results = [];

    for (let i = 0; i < this.targetCount(); i++) {
      try {
        const targetObject = this.objectCopyService.createSuperObjectModel(
          this.sectionsContainer(),
          this.sourceArtifactId(),
          this.modelSchema(),
          this.clientSettings()
        );

        // Check for identification number availability when auto-generating identifiers
        if (targetObject?.identification_number?.identification_number === '_IGNORE_') {
          const nextIdentifier = await this.cmsApi.getNextIdentifier({
            superobject_type_id: targetObject.superobject_type_id,
            collection_id: targetObject?.collection?.collection_id,
            // @ts-ignore
            context_id: targetObject?.identification_number?.context_id,
          } as GenerateIdentifierParams)
          if (!nextIdentifier.identifier) {
            console.warn('Exhausted identifier range for object type', targetObject.object_type);
            break;
          }
        }

        const storeResult = await this.objectStoreService.storeObject(
          this.objectStoreService.getCleanObject(targetObject)
        );
        const newObject = await this.objectStoreService.loadObject(storeResult.artifact_id);
        results.push(newObject);
      } catch (error) {
        console.error(error)
        this.errorMessage.set(
          error?.errorMessage || this.translate.instant('TRANS__ERROR__SAVING_ARTIFACT')
        );
        break;
      }
    }

    this.loading.set(false);

    if (this.errorMessage()) {
      return;
    }

    // Created copies passed to the callback defined in `EditFieldSearchSelectorHandler.openCreateNew`,
    // where they are finally added as child items to the parent object.
    this.dialogRef().close(results);

    // Check if some copies failed to be created.
    if (results.length < this.targetCount()) {
      this.dialog.open(AlertDialogComponent, {
        panelClass: 'alert-dialog',
        data: {
          modalTitle: 'TRANS__EDIT_OBJECT_DIALOG__STORE_WARNING',
          modalContent: this.translate.instant(
            'TRANS__EDIT_OBJECT_DIALOG__STORE_FAILED_INCOMPLETE', {
              count: results.length,
              target: this.targetCount(),
            }),
          modalButton: 'TRANS__ALERT_DIALOG__OK',
        } as ConfirmDialogData
      });
    }
  }

  getFieldIsRequired(field: MetaField): boolean {
    return field.is_required === 'is_required' || field.key === 'identification_number';
  }
}
